import React from 'react';
import Styled from 'styled-components';
import {FormattedMessage} from 'react-intl';
import {Modal, Icon, Text, FlexRow} from '@forecast-it/design-system';

interface ProjectCreationChoiceModalProps {
	closeModal: () => void;
	onManual: () => void;
	onAI: () => void;
	aiEnabled?: boolean;
}

const TitleDot = Styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6e0fea 0%, #9747ff 100%);
  flex-shrink: 0;
`;

const AccentBanner = Styled.div`
  background: linear-gradient(135deg, rgba(110, 15, 234, 0.04) 0%, rgba(57, 38, 100, 0.02) 100%);
  border-bottom: 1px solid rgba(110, 15, 234, 0.08);
  padding: 12px 32px;
`;

const Body = Styled.div`
  padding: 32px;
  display: flex;
  gap: 20px;
`;

const ChoiceCard = Styled.button<{highlight?: boolean}>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 24px;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  border: 1.5px solid ${({highlight}) => (highlight ? '#6e0fea' : '#e0e0e8')};
  background: ${({highlight}) =>
		highlight
			? 'linear-gradient(135deg, rgba(110,15,234,0.1) 0%, rgba(110,15,234,0.05) 100%)'
			: 'linear-gradient(135deg, #fafafa 0%, #f3f3f7 100%)'};

  &:hover {
    border-color: ${({highlight}) => (highlight ? '#560eb3' : '#c0bdd0')};
    background: ${({highlight}) =>
		highlight
			? 'linear-gradient(135deg, rgba(110,15,234,0.15) 0%, rgba(110,15,234,0.08) 100%)'
			: 'linear-gradient(135deg, #f3f3f7 0%, #efeff4 100%)'};
    transform: translateY(-2px);
    box-shadow: ${({highlight}) => (highlight ? '0 8px 24px rgba(110, 15, 234, 0.15)' : '0 8px 24px rgba(0,0,0,0.06)')};
  }

  &:active {
    transform: translateY(0);
  }
`;

const IconCircle = Styled.div<{highlight?: boolean}>`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({highlight}) =>
		highlight ? 'linear-gradient(135deg, #6e0fea 0%, #8a2be2 100%)' : 'linear-gradient(135deg, #f0ebf8 0%, #ede8f5 100%)'};
  flex-shrink: 0;
  color: ${({highlight}) => (highlight ? '#fff' : '#6e0fea')};
  box-shadow: ${({highlight}) => (highlight ? '0 4px 16px rgba(110, 15, 234, 0.25)' : '0 2px 8px rgba(110, 15, 234, 0.08)')};
  transition: all 0.2s ease;
`;

const ChoiceTitle = Styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #2e2541;
  letter-spacing: -0.3px;
`;

const ChoiceBadge = Styled.span`
  display: inline-block;
  background: linear-gradient(90deg, #6e0fea 0%, #7e2ef5 100%);
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 12px;
  margin-left: 8px;
  vertical-align: middle;
  box-shadow: 0 2px 8px rgba(110, 15, 234, 0.3);
`;

const ChoiceDesc = Styled.div`
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  font-weight: 500;
`;

const Divider = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  flex-shrink: 0;
  color: #b0a8c8;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 1px;
    height: 100%;
    background: linear-gradient(180deg, transparent 0%, #ddd 50%, transparent 100%);
  }
`;

const ProjectCreationChoiceModal: React.FC<ProjectCreationChoiceModalProps> = ({
	closeModal,
	onManual,
	onAI,
	aiEnabled = false,
}) => {
	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>
				<FlexRow gap="xs" alignItems="center">
					<TitleDot />
					<FormattedMessage id="project_creation_choice_modal.title" />
				</FlexRow>
			</Modal.Title>
			<Modal.Content style={{padding: '0'}}>
				<AccentBanner>
					<Text color="secondary" size={'2'}>
						<FormattedMessage id="project_creation_choice_modal.subtitle" />
					</Text>
				</AccentBanner>
				<Body>
					<ChoiceCard data-cy="project-creation-choice-scratch" onClick={onManual}>
						<IconCircle>
							<Icon icon="project" size="m" />
						</IconCircle>
						<div>
							<ChoiceTitle>
								<FormattedMessage id="project_creation_choice_modal.scratch_title" />
							</ChoiceTitle>
							<ChoiceDesc>
								<FormattedMessage id="project_creation_choice_modal.scratch_desc" />
							</ChoiceDesc>
						</div>
					</ChoiceCard>

					{aiEnabled && (
						<>
							<Divider>
								<FormattedMessage id="project_creation_choice_modal.divider" />
							</Divider>

							<ChoiceCard highlight onClick={onAI}>
								<IconCircle highlight>
									<Icon icon="sparkle" size="m" color="currentColor" />
								</IconCircle>
								<div>
									<ChoiceTitle>
										<FormattedMessage id="project_creation_choice_modal.ai_title" />
										<ChoiceBadge>
											<FormattedMessage id="project_creation_choice_modal.ai_badge" />
										</ChoiceBadge>
									</ChoiceTitle>
									<ChoiceDesc>
										<FormattedMessage id="project_creation_choice_modal.ai_desc" />
									</ChoiceDesc>
								</div>
							</ChoiceCard>
						</>
					)}
				</Body>
			</Modal.Content>
		</Modal>
	);
};

export default ProjectCreationChoiceModal;
